import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { DesktopPage } from '../../pages/NoTelegramPage/NoTelegramPage';
import type {
  ThemeParams,
  TelegramContextType,
  WebApp,
  SafeAreaInsets,
} from '../../types/telegram';
import { useTelegramUserStore } from '../../store/useTelegramUserStore';
import { useNavMenuStore } from '../../store/useNavMenuStore';

// Константы высот компонентов
// const PROFILE_MENU_HEIGHT = 0; // Высота ProfileMenu в пикселях
const NAV_MENU_HEIGHT = 56 + 16; // Высота NavMenu в пикселях

declare global {
  interface Window {
    Telegram?: {
      WebApp: WebApp;
    };
  }
}

export const TelegramContext = createContext<TelegramContextType>({
  webApp: null,
  userId: null,
  launchParams: {},
  isAllowed: false,
  platform: null,
  themeParams: null,
  setTheme: () => {},
  exitFullscreen: () => {},
  isFullscreen: false,
  isTelegram: false,
  systemInsets: { top: 0, bottom: 0, left: 0, right: 0 },
  contentInsets: { top: 0, bottom: 0, left: 0, right: 0 },
});

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
};

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [webApp, setWebApp] = useState<any>(null);
  const [isAllowed, setIsAllowed] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);
  const [launchParams, setLaunchParams] = useState<any>({});
  const [themeParams, setThemeParams] = useState<ThemeParams | null>(null);
  const [isTelegram, setIsTelegram] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [systemInsets, setSystemInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const [contentInsets, setContentInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const handlersRef = useRef<{
    handleSafeAreaChange?: (insets: SafeAreaInsets) => void;
    handleContentSafeAreaChange?: (insets: SafeAreaInsets) => void;
  }>({});
  const setUser = useTelegramUserStore((state) => state.setUser);

  // Получаем состояние видимости меню
  const isNavMenuVisible = useNavMenuStore(
    (state: { isVisible: boolean }) => state.isVisible,
  );

  const applyBodyInsets = (top: number, bottom: number) => {
    const root = document.getElementById('root');
    if (root) {
      const adjustedTop = top;
      // Добавляем высоту NavMenu к нижнему отступу, если он видим
      const adjustedBottom = bottom + (isNavMenuVisible ? NAV_MENU_HEIGHT : 0);

      // Устанавливаем отступы для root
      root.style.paddingTop = `${adjustedTop}px`;
      // root.style.paddingBottom = `${adjustedBottom}px`;

      // Устанавливаем стили для body
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.minHeight = '100vh';
    }
  };

  const setTheme = (newTheme: Partial<ThemeParams>) => {
    setThemeParams((prev) => (prev ? { ...prev, ...newTheme } : null));
    if (window.Telegram?.WebApp) {
      Object.entries(newTheme).forEach(([key, value]) => {
        if (value) {
          if (key === 'headerBackgroundColor') {
            window.Telegram?.WebApp?.setHeaderColor(value as `#${string}`);
          }
        }
      });
    }
  };

  if (window.Telegram?.WebApp) {
    const tg = window.Telegram.WebApp;

    if (tg.platform == 'ios' || tg.platform == 'android') {
      tg.requestFullscreen();
    }
  }
  const exitFullscreen = () => {
    if (window.Telegram?.WebApp) {
      try {
        const tg = window.Telegram.WebApp;
        if (typeof tg.exitFullscreen === 'function') {
          tg.exitFullscreen();
        }
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
      }
    }
  };

  const debugElementIds = [
    'system-safe-area',
    'content-safe-area',
    'bottom-system-safe-area',
    'bottom-content-safe-area',
  ] as const;

  useEffect(() => {
    // Обновляем отступы при изменении видимости меню
    const initialTop = systemInsets.top + contentInsets.top;
    const initialBottom = systemInsets.bottom + contentInsets.bottom;
    applyBodyInsets(initialTop, initialBottom);

    // Добавляем обработчик изменения размера окна
    const handleResize = () => {
      applyBodyInsets(initialTop, initialBottom);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isNavMenuVisible, systemInsets, contentInsets]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-web-app.js?57';
    script.async = true;
    script.onload = () => {
      const tg = window.Telegram?.WebApp;

      if (!tg?.initDataUnsafe?.user) {
        setIsTelegram(false);
        return;
      }

      // Проверяем платформу - блокируем доступ для десктопных платформ
      const isDesktopPlatform =
        tg.platform === 'tdesktop' ||
        tg.platform === 'macos' ||
        tg.platform === 'web';

      const isSpecialUser =
        tg.initDataUnsafe?.user?.id === 6944819896 ||
        tg.initDataUnsafe?.user?.id === 8075707358 ||
        tg.initDataUnsafe?.user?.id === 353357144 ||
        tg.initDataUnsafe?.user?.id === 337937791 ||
        tg.initDataUnsafe?.user?.id === 606261842 ||
        tg.initDataUnsafe?.user?.id === 508501888;

      if (isDesktopPlatform && !isSpecialUser) {
        setIsTelegram(false);
        return;
      }

      setWebApp(tg);
      setIsTelegram(true);
      const DesktopPadding = isDesktopPlatform;
      const platform = tg.platform;
      const initialSystemInsets: SafeAreaInsets = {
        top: DesktopPadding ? 0 : platform === 'ios' ? 47 : 24,
        bottom: DesktopPadding ? 0 : platform === 'ios' ? 34 : 23,
        left: 0,
        right: 0,
      };
      const initialContentInsets: SafeAreaInsets = {
        top: DesktopPadding ? 0 : 56,
        bottom: 0,
        left: 0,
        right: 0,
      };

      setSystemInsets(initialSystemInsets);
      setContentInsets(initialContentInsets);

      // Применяем начальные отступы
      const initialTop = initialSystemInsets.top + initialContentInsets.top;
      const initialBottom =
        initialSystemInsets.bottom + initialContentInsets.bottom;
      applyBodyInsets(initialTop, initialBottom);
      // updateDebugElements(initialSystemInsets, initialContentInsets);
      tg.onEvent('fullscreenChanged', () => {
        setIsFullscreen(tg.isFullscreen);
      });

      tg.onEvent(
        'fullscreenFailed',
        (event: { error: 'UNSUPPORTED' | 'ALREADY_FULLSCREEN' }) => {
          console.error('Fullscreen failed:', event.error);
        },
      );

      setIsFullscreen(tg.isFullscreen);
      tg.setBackgroundColor('#1c1c1c');
      tg.setHeaderColor('#1c1c1c');

      const user = tg.initDataUnsafe?.user;
      if (user) {
        console.log('Raw Telegram User Data:', user);
        setUserId(user.id);
        setIsAllowed(true);
        setUser(user);
      }

      setPlatform(tg.platform);
      setLaunchParams(tg.initDataUnsafe || {});
      setThemeParams(tg.themeParams as ThemeParams);
      tg.disableVerticalSwipes();
      tg.expand();
      tg.ready();
    };
    document.head.appendChild(script);
    return () => {
      if (window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        if (handlersRef.current.handleSafeAreaChange) {
          tg.offEvent(
            'safe_area_changed',
            handlersRef.current.handleSafeAreaChange,
          );
        }
        if (handlersRef.current.handleContentSafeAreaChange) {
          tg.offEvent(
            'content_safe_area_changed',
            handlersRef.current.handleContentSafeAreaChange,
          );
        }
      }
      document.head.removeChild(script);
      document.body.style.paddingTop = '';
      document.body.style.paddingBottom = '';
      debugElementIds.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          element.remove();
        }
      });
    };
  }, [setUser]);

  if (!webApp) {
    return <DesktopPage />;
  }

  return (
    <TelegramContext.Provider
      value={{
        webApp,
        userId,
        launchParams,
        isAllowed,
        platform,
        themeParams,
        setTheme,
        exitFullscreen,
        isFullscreen,
        isTelegram,
        systemInsets,
        contentInsets,
      }}
    >
      {children}
    </TelegramContext.Provider>
  );
};
